"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { ArrowLeft } from "lucide-react";
const wp_query_params = {
  per_page: 8,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function TablesGridConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="100178" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
        {[...Array(8)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <Skeleton className="w-full aspect-[4/3] rounded-xl bg-muted/20" />
            <Skeleton className="h-6 w-3/4 bg-muted/20" />
            <Skeleton className="h-5 w-1/4 bg-muted/20" />
            <Skeleton className="h-10 w-full mt-2 bg-muted/20" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-destructive">
        <p className="font-default text-lg">Failed to load the cityscape models. Please try again later.</p>
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p className="font-default text-lg">No interactive cities available for preview at this time.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
      {products.map((product, i) => <Card key={product.id} data-index={i} className="overflow-hidden border border-border bg-card transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/10 flex flex-col rounded-xl">
          <Link to={product.link} className="block overflow-hidden bg-muted/10">
            <AspectRatio ratio={4 / 3}>
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="object-cover w-full h-full transition-transform duration-500 hover:scale-110 opacity-90 hover:opacity-100" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center bg-muted/20 text-muted-foreground font-default text-sm uppercase tracking-wider">
                  Rendering...
                </div>}
            </AspectRatio>
          </Link>
          
          <CardContent className="p-5 flex flex-col flex-grow">
            <Link to={product.link} className="mb-2 inline-block">
              <h3 className="text-xl font-default font-bold text-foreground line-clamp-2 hover:text-primary transition-colors tracking-tight">
                {product.title}
              </h3>
            </Link>
            
            {product.price !== undefined && <div className="text-lg font-default font-semibold text-primary mb-4 tracking-tight">
                ${product.price.toFixed(2)} / project
              </div>}
            
            <div className="mt-auto flex flex-col gap-3 pt-4">
              <AddToCartButton productId={product.id} className="w-full" buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 w-full font-default font-bold text-base h-11 rounded-lg transition-colors" text="Access Model" showQuantity={false} />
              <Button asChild variant="outline" className="w-full h-11 border-border hover:bg-secondary hover:text-secondary-foreground rounded-lg font-default transition-all">
                <Link to={product.link}>
                  View Details
                  <ArrowLeft className="w-4 h-4 mr-2 rotate-180" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>)}
    </div>;
}
export default function Tables() {
  return <section id="interactive-cityscapes" className="py-24 md:py-32 bg-background text-foreground dark">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="mb-16 md:mb-20 text-center max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-foreground mb-6 tracking-tighter">
            High-Quality, Interactive Cities
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
            Explore our immersive digital twins and interactive 3D urban environments. Powered by spatial intelligence to transform how you visualize property and infrastructure at scale.
          </p>
        </div>
        
        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        order: "desc",
        orderby: "date"
      }}>
          <TablesGridConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}